import {StateLabel} from "@platform/models/state-labels/state-label"
import {ResourceGroupId} from "@generated"

// Batched queue-group filter options map a synthetic key to several real queue groups; the
// jobs filter expands them when building the query. Options not listed here pass through as-is.
export const JobQueueGroupFilterValues: Record<string, string[]> = {
    PowerMachine: ["colormass/compute-engine/power-machine/render", "colormass-gce/render", "colormass-cloud-gce/render"],
    CloudRunGpuRender: ["colormass/cloud-run/gpu/render", "colormass-cloud-gpu/render"],
}

export const JobQueueGroupLabels: Map<string, StateLabel<string>> = new Map([
    ["CloudRunGpuRender", {state: "CloudRunGpuRender", label: "Cloud Run GPU"}],
    ["high-memory", {state: "high-memory", label: "Cloud Run High Memory"}],
    ["low-memory", {state: "low-memory", label: "Cloud Run Low Memory"}],
    ["mid-memory", {state: "mid-memory", label: "Cloud Run Mid Memory"}],
    ["colormass/compute-engine/gpu/render", {state: "colormass/compute-engine/gpu/render", label: "Compute Engine GPU"}],
    [
        "colormass/compute-engine/high-memory/image-processing",
        {state: "colormass/compute-engine/high-memory/image-processing", label: "Compute Engine High Memory"},
    ],
    ["PowerMachine", {state: "PowerMachine", label: "Compute Engine Power Machine"}],
])

/**
 * Labels for the resource-group pin on the job task details dialog. Keyed by the typed {@link ResourceGroupId}
 * — the pin names the exact resource group a JobsV2 task is placed on, so the key set is the enum itself
 * rather than a free-text queue group (which could not disambiguate the shared `tasks-main` services).
 *
 * Distinct from {@link JobQueueGroupLabels} above, which labels the jobs *filter*. Each entry here is a real
 * destination a task can be pinned to, named for the machine it lands on.
 */
export const JobResourceGroupPinLabels: Map<ResourceGroupId, StateLabel<ResourceGroupId>> = new Map<ResourceGroupId, StateLabel<ResourceGroupId>>([
    [ResourceGroupId.ComputeEngineGpuRendering, {state: ResourceGroupId.ComputeEngineGpuRendering, label: "Compute Engine GPU (spot)"}],
    [ResourceGroupId.ComputeEngineGpuRenderingOnDemand, {state: ResourceGroupId.ComputeEngineGpuRenderingOnDemand, label: "Compute Engine GPU (on-demand)"}],
    [ResourceGroupId.ComputeEngineGpuRenderingHighMemory, {state: ResourceGroupId.ComputeEngineGpuRenderingHighMemory, label: "Compute Engine GPU (high RAM)"}],
    [ResourceGroupId.ComputeEnginePowerMachine, {state: ResourceGroupId.ComputeEnginePowerMachine, label: "Power Machine (spot)"}],
    [ResourceGroupId.ComputeEnginePowerMachineFlexStart, {state: ResourceGroupId.ComputeEnginePowerMachineFlexStart, label: "Power Machine (flex-start)"}],
    [ResourceGroupId.ComputeEnginePowerMachineOnDemand, {state: ResourceGroupId.ComputeEnginePowerMachineOnDemand, label: "Power Machine (on-demand)"}],
    [ResourceGroupId.ComputeEngineImageProcessing, {state: ResourceGroupId.ComputeEngineImageProcessing, label: "Compute Engine Image Processing"}],
    [ResourceGroupId.ComputeEngineMaterialAi, {state: ResourceGroupId.ComputeEngineMaterialAi, label: "Compute Engine Material AI"}],
    [ResourceGroupId.CloudRunGpuRendering, {state: ResourceGroupId.CloudRunGpuRendering, label: "Cloud Run GPU"}],
    [ResourceGroupId.CloudRunCpuRendering, {state: ResourceGroupId.CloudRunCpuRendering, label: "Cloud Run CPU"}],
    [ResourceGroupId.CloudRunHighMemory, {state: ResourceGroupId.CloudRunHighMemory, label: "Cloud Run High Memory"}],
    [ResourceGroupId.CloudRunMidMemory, {state: ResourceGroupId.CloudRunMidMemory, label: "Cloud Run Mid Memory"}],
    [ResourceGroupId.CloudRunLowMemory, {state: ResourceGroupId.CloudRunLowMemory, label: "Cloud Run Low Memory"}],
    [ResourceGroupId.CloudRunTiling, {state: ResourceGroupId.CloudRunTiling, label: "Cloud Run Tiling"}],
    [ResourceGroupId.CloudRunPythonTasks, {state: ResourceGroupId.CloudRunPythonTasks, label: "Cloud Run Python Tasks"}],
    [ResourceGroupId.CloudRunArGltf, {state: ResourceGroupId.CloudRunArGltf, label: "Cloud Run AR glTF"}],
    [ResourceGroupId.CloudRunArUsdz, {state: ResourceGroupId.CloudRunArUsdz, label: "Cloud Run AR USDZ"}],
    [ResourceGroupId.RenderFarmCpuRendering, {state: ResourceGroupId.RenderFarmCpuRendering, label: "Render Farm CPU"}],
    [ResourceGroupId.RenderFarmGpuRendering, {state: ResourceGroupId.RenderFarmGpuRendering, label: "Render Farm GPU"}],
])

/**
 * The resource groups the pin dropdown offers, in display order. A subset of {@link JobResourceGroupPinLabels}
 * (which keeps every group so an existing pin always renders a label): only groups a task can be
 * *meaningfully* re-placed onto — the render tiers (GPU spot / on-demand / high RAM / Cloud Run, power machine spot /
 * flex-start / on-demand) and the image-processing tiers (GCE image processing vs Cloud Run low/mid/high
 * memory). Single-purpose groups (material-ai, tiling, python-tasks, AR, render farm) are omitted: pinning
 * them changes nothing useful, since no other group serves their work.
 */
export const JobResourceGroupPinOptions: ResourceGroupId[] = [
    ResourceGroupId.ComputeEngineGpuRendering,
    ResourceGroupId.ComputeEngineGpuRenderingOnDemand,
    ResourceGroupId.ComputeEngineGpuRenderingHighMemory,
    ResourceGroupId.CloudRunGpuRendering,
    ResourceGroupId.ComputeEnginePowerMachine,
    ResourceGroupId.ComputeEnginePowerMachineFlexStart,
    ResourceGroupId.ComputeEnginePowerMachineOnDemand,
    ResourceGroupId.ComputeEngineImageProcessing,
    ResourceGroupId.CloudRunHighMemory,
    ResourceGroupId.CloudRunMidMemory,
    ResourceGroupId.CloudRunLowMemory,
]
